import React, { useState } from 'react';
import Icon from 'components/styleguide/icon/Icon';
import Tag from 'components/styleguide/tag/Tag';
import Button from '../../../lj_shared/button/button';
import RequestInfoModal from '../../../curators/request_info_modal';

const styles = require('./CandidateCard.module.scss');

export interface Website {
  category: string;
  url: string;
}

export interface Props {
  applicationId: number | string;
  applicationKind: string;
  avatarHtml?: string | null;
  companyName?: string | null;
  email?: string | null;
  jobTitle?: string | null;
  jobUrl?: string | null;
  name: string;
  requestInfo?: unknown;
  requestInfoAlreadyMade: boolean;
  urls: { cv?: string | null; [key: string]: unknown };
  websites?: Website[] | null;
}

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

// The candidate header card: identity on the left, the candidate's artifacts
// (CV, LinkedIn, email) as link tiles on the right, with the existing
// request-more-info flow.
export default function CandidateCard({
  applicationId,
  applicationKind,
  avatarHtml = null,
  companyName = null,
  email = null,
  jobTitle = null,
  jobUrl = null,
  name,
  requestInfo,
  requestInfoAlreadyMade,
  urls,
  websites = null,
}: Props) {
  const [requestMade, setRequestMade] = useState(requestInfoAlreadyMade);

  const linkedin = (websites || []).find((website) => website.category === 'linkedin');

  const showModal = (event: React.MouseEvent) => {
    event.preventDefault();
    RequestInfoModal.show(applicationId, name, urls, () => setRequestMade(true), requestInfo);
  };

  return (
    <div className={styles['ui-candidate-card']}>
      <span aria-hidden className={styles['ui-candidate-card__avatar']}>
        {avatarHtml ? (
          // The avatar presenter ships prebuilt markup from the server.
          <span dangerouslySetInnerHTML={{ __html: avatarHtml }} />
        ) : (
          <span className={styles['ui-candidate-card__initials']}>{initials(name)}</span>
        )}
      </span>

      <div className={styles['ui-candidate-card__identity']}>
        <span className={styles['ui-candidate-card__name']}>{name}</span>
        {jobTitle && jobUrl && (
          <a
            className={styles['ui-candidate-card__job-link']}
            href={jobUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            {jobTitle}
            {companyName ? ` @ ${companyName}` : ''}
          </a>
        )}
        <span className={styles['ui-candidate-card__kind-row']}>
          <Tag className={styles['ui-candidate-card__kind']} variant="info">
            {applicationKind}
          </Tag>
        </span>
      </div>

      <div className={styles['ui-candidate-card__links']}>
        {urls.cv && (
          <a
            className={styles['ui-candidate-card__tile']}
            href={urls.cv as string}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Icon aria-hidden name="fileText" size="sm" />
            <span>CV</span>
          </a>
        )}
        {linkedin && (
          <a
            className={styles['ui-candidate-card__tile']}
            href={linkedin.url}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Icon aria-hidden name="linkedin" size="sm" />
            <span>LinkedIn</span>
          </a>
        )}
        {email && (
          <a
            aria-label={`Email ${email}`}
            className={styles['ui-candidate-card__tile']}
            href={`mailto:${email}`}
          >
            <Icon aria-hidden name="mail" size="sm" />
            <span>Email</span>
          </a>
        )}
        {requestMade ? (
          <Button buttonType='border' buttonSize='xSmall' buttonColor='silverSand' isButton={false} onClick={showModal}>
            Request info already sent
          </Button>
        ) : (
          <Button buttonType='border' buttonSize='xSmall' isButton={false} onClick={showModal}>
            Request more info
          </Button>
        )}
      </div>
    </div>
  );
}
